import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { FaUserPlus, FaShare, FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

function ShareDocument({ documentId }) {
  const { authState } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/all?page=${page}&limit=5`,
          {
            headers: {
              "x-auth-token": authState.token,
            },
          }
        );
        const newUsers = response.data;
        if (Array.isArray(newUsers)) {
          setUsers((prevUsers) => [
            ...prevUsers,
            ...newUsers.filter(
              (user) => !prevUsers.some((prevUser) => prevUser._id === user._id)
            ),
          ]);
          setHasMore(newUsers.length === 5); // Continue loading if we received 5 users
        } else {
          console.error("Unexpected response format:", response.data);
          toast.error("Unexpected response format from the server.");
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Error fetching users.");
      }
    };

    fetchUsers();
  }, [authState.token, page]);

  const handleShare = async () => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}/share`,
        { collaborators: selectedUsers },
        {
          headers: {
            "x-auth-token": authState.token,
          },
        }
      );
      toast.success(response.data.message || "Document shared successfully!");
    } catch (error) {
      console.error("Error sharing document:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to share document.";
      toast.error(errorMessage);
    }
  };

  const filteredUsers = users.filter((user) =>
    user.firstname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow-md max-w-xs mx-auto p-4 space-y-4">
      <button
        onClick={() => navigate(-1)}
        style={{
          boxShadow: "11px 13px 28px 3px rgba(0,0,0,0.75)", // Custom Box Shadow
        }}
        className="flex items-center py-2 px-3 rounded-md hover:bg-black hover:text-white text-gray-600 text-xs font-medium mb-2"
      >
        <FaArrowLeft className="mr-1 text-sm" />
        Back
      </button>
      <h2 className="text-sm font-extrabold text-gray-800 flex items-center">
        <FaShare className="mr-1 text-sm" />
        Share Document
      </h2>
      <div className="relative mb-3">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-gray-500"
        />
        <FaUserPlus className="absolute right-2 top-2 text-gray-400 text-lg" />
      </div>
      <div className="space-y-2">
        {filteredUsers.length > 0 ? (
          filteredUsers.slice(0, 5).map((user) => (
            <div key={user._id} className="flex items-center mb-1">
              <input
                type="checkbox"
                id={user._id}
                value={user._id}
                checked={selectedUsers.includes(user._id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedUsers([...selectedUsers, user._id]);
                  } else {
                    setSelectedUsers(
                      selectedUsers.filter((id) => id !== user._id)
                    );
                  }
                }}
                className="mr-2 form-checkbox h-4 w-4 text-gray-600"
              />
              <label htmlFor={user._id} className="text-gray-700 text-xs">
                {user.firstname}
              </label>
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-xs">No users found.</p>
        )}
      </div>
      {hasMore && (
        <button
          onClick={() => setPage((prevPage) => prevPage + 1)}
          style={{
            boxShadow: "11px 13px 28px 3px rgba(0,0,0,0.75)", // Custom Box Shadow
          }}
          className="w-full bg-gray-200 text-gray-700 text-xs font-medium py-1 px-3 rounded-md hover:bg-gray-300 focus:outline-none"
        >
          Load More
        </button>
      )}
      <button
        onClick={handleShare}
        style={{
          boxShadow: "11px 13px 28px 3px rgba(0,0,0,0.75)", // Custom Box Shadow
        }}
        className="w-full bg-black text-white text-xs font-medium py-2 px-3 rounded-md hover:bg-gray-700 focus:outline-none flex items-center justify-center"
      >
        <FaShare className="mr-1 text-sm" />
        Share
      </button>
    </div>
  );
}

export default ShareDocument;
